/**
 * HomeSections.js
 * New homepage sections for IMPORTREND redesign.
 * All sections accept an optional `config` prop from the homepage API.
 * Hardcoded defaults ensure sections always render even without config.
 * Sections: CategoryIconsRow, ShopByPrice, FlashDeals, PromoBannersNew,
 *           NewArrivals, FeaturedCategories, WhyShop, TopDealsWeek,
 *           ResellerApp, CustomerReviews
 */
import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  ChevronRight, ChevronLeft, ShoppingCart, Star, Truck, Package,
  BadgePercent, ShieldCheck, RotateCcw, Headphones, Zap, Play,
  Monitor, Smartphone, Home, UtensilsCrossed, Shirt, Sparkles,
  BookOpen, Gamepad2, Activity, Car, Heart, Leaf, Download,
  CheckCircle2, Apple, Gift, X,
} from "lucide-react";
import { addToCart } from "@/utils/cart";

import { deviceClass } from "./CustomSections";

const fmt = (n) => Number(n || 0).toLocaleString("en-IN");
const disc = (mrp, price) => (mrp > price ? Math.round(((mrp - price) / mrp) * 100) : 0);

// ── Icon map for dynamic icon rendering ─────────────────────────────────
const ICON_MAP = {
  Truck, Package, BadgePercent, ShieldCheck, RotateCcw, Headphones, Heart, Star, Zap, Gift,
  Monitor, Smartphone, Home, UtensilsCrossed, Shirt, Sparkles, BookOpen, Gamepad2, Activity,
  Car, Leaf, ShoppingCart, ChevronRight, ChevronLeft, Play, Download, CheckCircle2, Apple,
};

// ── Avatar helpers for dynamic customer reviews ──────────────────────────
const AVATAR_COLORS = ["bg-pink-500", "bg-blue-500", "bg-purple-500", "bg-green-500", "bg-orange-500", "bg-teal-500"];
const getAvatar = (name) => (name || "?").split(" ").map(n => n[0]).slice(0, 2).join("").toUpperCase();

// ── Badge color helper for Shop by Price dynamic buckets ─────────────────
const BADGE_COLOR_MAP = {
  "from-orange-400": "bg-orange-500", "from-purple-500": "bg-purple-600",
  "from-blue-500": "bg-blue-600", "from-emerald-500": "bg-emerald-600",
  "from-rose-500": "bg-rose-500", "from-slate-700": "bg-slate-800",
  "from-amber-500": "bg-amber-600", "from-teal-500": "bg-teal-600",
};
const getBadgeColor = (gradient = "") => BADGE_COLOR_MAP[gradient.split(" ")[0]] || "bg-black/30";

// ── Compact Product Card used across flash deals / new arrivals ────────────
function MiniProductCard({ product, showAdd = true }) {
  const price = product.sale_price || product.price || 0;
  const mrp = product.mrp || price;
  const d = disc(mrp, price);
  const img = product.images?.[0] || product.image || "";

  const handleAdd = (e) => {
    e.preventDefault();
    addToCart({ ...product, qty: 1 });
    window.dispatchEvent(new Event("cart-updated"));
  };

  return (
    <div className="flex-shrink-0 w-44 bg-white rounded-xl border border-slate-100 overflow-hidden shadow-sm hover:shadow-md transition-shadow group">
      <Link to={`/product/${product.id}`}>
        <div className="relative">
          <div className="aspect-square bg-slate-50 overflow-hidden">
            {img ? (
              <img src={img} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" decoding="async" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-slate-300 text-4xl">🛍</div>
            )}
          </div>
          {d >= 5 && (
            <span className="absolute top-2 left-2 bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded">
              -{d}%
            </span>
          )}
        </div>
        <div className="p-2.5">
          <p className="text-xs text-slate-700 font-medium line-clamp-2 leading-tight mb-1 min-h-[2.5rem]">
            {product.name}
          </p>
          <div className="flex items-center gap-1 mb-1.5">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className={`w-2.5 h-2.5 ${i < Math.round(product.rating || 4) ? "text-amber-400 fill-amber-400" : "text-slate-200 fill-slate-200"}`} />
            ))}
            {product.review_count > 0 && (
              <span className="text-[9px] text-slate-400">({product.review_count})</span>
            )}
          </div>
          <div className="flex items-baseline gap-1.5">
            <span className="text-sm font-black text-slate-900">₹{fmt(price)}</span>
            {mrp > price && <span className="text-[10px] text-slate-400 line-through">₹{fmt(mrp)}</span>}
          </div>
        </div>
      </Link>
      {showAdd && (
        <button
          onClick={handleAdd}
          className="w-full flex items-center justify-center gap-1 text-xs font-semibold text-white bg-orange-500 hover:bg-orange-600 transition-colors py-2"
        >
          <ShoppingCart className="w-3 h-3" /> Add to Cart
        </button>
      )}
    </div>
  );
}

// ── Section Header ─────────────────────────────────────────────────────────
function SectionHeader({ icon: Icon, title, subtitle, viewAllTo, flash = false, showTitle = true, showSubtitle = true }) {
  if (!showTitle && !(showSubtitle && subtitle) && !viewAllTo) return null;
  return (
    <div className="flex items-end justify-between mb-4">
      <div>
        {showTitle && (
          <div className="flex items-center gap-2 mb-0.5">
            {flash && <Zap className="w-5 h-5 text-orange-500 fill-orange-500" />}
            <h2 className="text-xl font-black text-slate-900">{title}</h2>
          </div>
        )}
        {showSubtitle && subtitle && <p className="text-xs text-slate-500">{subtitle}</p>}
      </div>
      {viewAllTo && (
        <Link to={viewAllTo} className="text-xs font-semibold text-blue-600 hover:underline flex items-center gap-0.5 whitespace-nowrap">
          View All <ChevronRight className="w-3.5 h-3.5" />
        </Link>
      )}
    </div>
  );
}

// ── Horizontal Scroll Carousel ─────────────────────────────────────────────
function HScrollCarousel({ children, className = "" }) {
  const ref = useRef(null);
  const scroll = (dir) => {
    if (ref.current) ref.current.scrollBy({ left: dir * 200, behavior: "smooth" });
  };
  return (
    <div className="relative group">
      <button
        onClick={() => scroll(-1)}
        className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-white shadow-lg rounded-full flex items-center justify-center border border-slate-100 opacity-0 group-hover:opacity-100 transition-opacity -translate-x-1/2"
      >
        <ChevronLeft className="w-4 h-4 text-slate-600" />
      </button>
      <div ref={ref} className={`flex gap-3 overflow-x-auto scrollbar-hide scroll-smooth ${className}`}>
        {children}
      </div>
      <button
        onClick={() => scroll(1)}
        className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-white shadow-lg rounded-full flex items-center justify-center border border-slate-100 opacity-0 group-hover:opacity-100 transition-opacity translate-x-1/2"
      >
        <ChevronRight className="w-4 h-4 text-slate-600" />
      </button>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// 1. CATEGORY ICONS ROW
// ──────────────────────────────────────────────────────────────────────────
const CAT_ICONS = [
  { label: "Electronics", Icon: Monitor, color: "bg-blue-100 text-blue-600", slug: "electronics" },
  { label: "Mobile Accessories", Icon: Smartphone, color: "bg-purple-100 text-purple-600", slug: "mobile-accessories" },
  { label: "Home & Living", Icon: Home, color: "bg-orange-100 text-orange-600", slug: "home-living" },
  { label: "Kitchen & Dining", Icon: UtensilsCrossed, color: "bg-red-100 text-red-600", slug: "kitchen-dining" },
  { label: "Fashion", Icon: Shirt, color: "bg-pink-100 text-pink-600", slug: "fashion" },
  { label: "Beauty & Personal Care", Icon: Sparkles, color: "bg-rose-100 text-rose-600", slug: "beauty" },
  { label: "Stationery", Icon: BookOpen, color: "bg-yellow-100 text-yellow-600", slug: "stationery" },
  { label: "Toys & Kids", Icon: Gamepad2, color: "bg-green-100 text-green-600", slug: "toys-kids" },
  { label: "Sports & Fitness", Icon: Activity, color: "bg-teal-100 text-teal-600", slug: "sports-fitness" },
  { label: "Automotive", Icon: Car, color: "bg-slate-100 text-slate-600", slug: "automotive" },
  { label: "Pet Supplies", Icon: Heart, color: "bg-red-100 text-red-500", slug: "pet-supplies" },
  { label: "Health & Wellness", Icon: Leaf, color: "bg-emerald-100 text-emerald-600", slug: "health-wellness" },
];

export function CategoryIconsRow({ config = {} }) {
  if (config.enabled === false) return null;

  // Use admin-configured items if available, otherwise fall back to hardcoded
  const items = (config.items && config.items.length > 0)
    ? config.items
    : CAT_ICONS.map(c => ({
        label: c.label,
        icon: Object.keys(ICON_MAP).find(k => ICON_MAP[k] === c.Icon) || "ShoppingCart",
        link: c.slug ? `/search?q=${encodeURIComponent(c.label)}` : `/search?q=${encodeURIComponent(c.label)}`,
        color: c.color,
        image_url: "",
      }));

  return (
    <div className="bg-white border-b border-slate-100 py-4">
      <div className="max-w-[1800px] mx-auto px-4">
        <div className="flex gap-4 overflow-x-auto scrollbar-hide">
          {items.map((item, i) => {
            const IconComp = ICON_MAP[item.icon] || ShoppingCart;
            return (
              <Link
                key={i}
                to={item.link || `/search?q=${encodeURIComponent(item.label)}`}
                className="flex flex-col items-center gap-2 flex-shrink-0 group"
              >
                <div className={`w-14 h-14 rounded-full ${item.color || "bg-slate-100 text-slate-600"} flex items-center justify-center group-hover:scale-110 transition-transform overflow-hidden`}>
                  {item.image_url ? (
                    <img src={item.image_url} alt={item.label} className="w-full h-full object-cover rounded-full" loading="lazy" decoding="async" />
                  ) : (
                    <IconComp className="w-6 h-6" />
                  )}
                </div>
                <span className="text-[11px] text-slate-600 font-medium text-center whitespace-nowrap max-w-[72px] leading-tight">
                  {!item.hide_label && item.label}
                </span>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// 2. SHOP BY PRICE
// ──────────────────────────────────────────────────────────────────────────
const PRICE_BUCKETS_DEFAULT = [
  { label: "Under ₹49",  sub: "Budget Buys",    max: 49,  gradient: "from-orange-400 to-amber-300",   badge: "bg-orange-500"  },
  { label: "Under ₹99",  sub: "Great Value",     max: 99,  gradient: "from-purple-500 to-violet-400",  badge: "bg-purple-600"  },
  { label: "Under ₹199", sub: "Most Popular",    max: 199, gradient: "from-blue-500 to-cyan-400",      badge: "bg-blue-600"    },
  { label: "Under ₹299", sub: "Premium Picks",   max: 299, gradient: "from-emerald-500 to-teal-400",   badge: "bg-emerald-600" },
  { label: "Under ₹499", sub: "Top Selections",  max: 499, gradient: "from-rose-500 to-pink-400",      badge: "bg-rose-500"    },
];

const isVideoUrl = (url) => url && /\.(mp4|webm|mov|ogv)(\?.*)?$/i.test(url);

// ── Reusable banner card (used in grid + modal) ────────────────────────────
function PriceBannerCard({ bucket }) {
  const { label, sub, max, gradient, image_url, show_shop_now = true, shop_now_color, show_text = true, link_url } = bucket;
  const badge    = bucket.badge || getBadgeColor(gradient);
  const hasMedia = !!image_url;
  const isVid    = hasMedia && isVideoUrl(image_url);
  // Overlay only needed when there's something to display over the image
  const needsOverlay = show_text !== false || show_shop_now !== false;
  const href = link_url || `/search?maxPrice=${max}`;
  // Absolute URLs (https://, //, mailto: …) must not go through React Router <Link>
  const isExternal = /^([a-z][a-z0-9+.-]*:|\/\/)/i.test(href);
  const LinkTag = isExternal ? "a" : Link;
  const linkProps = isExternal ? { href } : { to: href, onClick: () => window.scrollTo(0, 0) };

  return (
    <LinkTag
      {...linkProps}
      className={`relative rounded-2xl overflow-hidden aspect-[9/16] flex flex-col justify-end group hover:shadow-xl transition-shadow ${deviceClass(bucket.device, "flex")}`}
    >
      {bucket.tag && (
        <span className="absolute top-2 left-2 z-20 bg-red-500 text-white text-[10px] font-black px-2 py-0.5 rounded shadow" data-testid="banner-tag">{bucket.tag}</span>
      )}
      {hasMedia ? (
        isVid ? (
          <video src={image_url} autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <img src={image_url} alt={label} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" decoding="async" />
        )
      ) : (
        <div className={`absolute inset-0 bg-gradient-to-br ${gradient || "from-slate-500 to-slate-700"}`} />
      )}

      {/* Gradient overlay — only renders when text or button is visible */}
      {needsOverlay && (
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
      )}

      {/* Content */}
      {needsOverlay && (
        <div className="relative z-10 p-4">
          {show_text !== false && (
            <>
              <span className={`inline-block ${badge} text-white text-xs font-black px-2.5 py-1 rounded-lg mb-1.5 shadow`}>{label}</span>
              <p className="text-white/90 text-sm font-medium mb-3 leading-snug">{sub}</p>
            </>
          )}
          {show_shop_now !== false && (
            <span
              className={`inline-flex items-center gap-1 text-white text-xs font-semibold px-3 py-1.5 rounded-full transition-colors backdrop-blur-sm ${!shop_now_color ? "bg-white/20 hover:bg-white/30" : ""}`}
              style={shop_now_color ? { backgroundColor: shop_now_color } : undefined}
            >
              Shop Now <ChevronRight className="w-3 h-3" />
            </span>
          )}
        </div>
      )}
    </LinkTag>
  );
}

export function ShopByPriceSection({ config = {} }) {
  const [showAll, setShowAll] = useState(false);
  if (config.enabled === false) return null;

  const title       = config.title    || "Shop by Price";
  const subtitle    = config.subtitle || "Exciting deals at every budget";
  const buckets     = ((config.buckets?.length > 0) ? config.buckets : PRICE_BUCKETS_DEFAULT).filter(b => b.enabled !== false);
  const rows        = Math.max(1, config.rows || 1);
  const showCount   = rows * 5;
  const visible     = buckets.slice(0, showCount);
  const hasMore     = buckets.length > showCount;

  return (
    <div className="bg-white py-8">
      <div className="max-w-[1800px] mx-auto px-4">
        {/* Section header — "View All" opens modal when there are more than visible */}
        {(config.show_title !== false || (config.show_subtitle !== false && subtitle) || hasMore) && (
        <div className="flex items-end justify-between mb-4">
          <div>
            {config.show_title !== false && (
              <div className="flex items-center gap-2 mb-0.5">
                <Zap className="w-5 h-5 text-orange-500 fill-orange-500" />
                <h2 className="text-xl font-black text-slate-900">{title}</h2>
                {config.tag && <span className="bg-red-500 text-white text-[10px] font-black px-2 py-0.5 rounded" data-testid="section-tag">{config.tag}</span>}
              </div>
            )}
            {config.show_subtitle !== false && subtitle && (
              <p className="text-xs text-slate-500">{subtitle}</p>
            )}
          </div>
          {hasMore && (
            <button
              onClick={() => setShowAll(true)}
              className="text-xs font-semibold text-blue-600 hover:underline flex items-center gap-0.5 whitespace-nowrap"
            >
              View All <ChevronRight className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        )}

        {/* Always exactly 5 — never wraps to next row */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {visible.map((bucket, i) => <PriceBannerCard key={i} bucket={bucket} />)}
        </div>
      </div>

      {/* ── View All Modal ─────────────────────────────────────────────── */}
      {showAll && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-10 px-4"
          onClick={e => { if (e.target === e.currentTarget) setShowAll(false); }}
        >
          <div className="bg-white rounded-2xl w-full max-w-[1400px] shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 sticky top-0 bg-white z-10">
              <div>
                {config.show_title !== false && (
                  <div className="flex items-center gap-2">
                    <Zap className="w-5 h-5 text-orange-500 fill-orange-500" />
                    <h3 className="text-lg font-black text-slate-900">{title}</h3>
                  </div>
                )}
                {config.show_subtitle !== false && subtitle && (
                  <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>
                )}
              </div>
              <button
                onClick={() => setShowAll(false)}
                className="w-9 h-9 flex items-center justify-center rounded-full bg-slate-100 hover:bg-slate-200 transition-colors"
              >
                <X className="w-4 h-4 text-slate-600" />
              </button>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                {buckets.map((bucket, i) => <PriceBannerCard key={i} bucket={bucket} />)}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// 3. TODAY'S FLASH DEALS (with countdown timer)
// ──────────────────────────────────────────────────────────────────────────
function useCountdown(targetHours = 12) {
  const [time, setTime] = useState({ h: targetHours, m: 36, s: 59 });
  useEffect(() => {
    const t = setInterval(() => {
      setTime(prev => {
        let { h, m, s } = prev;
        s--;
        if (s < 0) { s = 59; m--; }
        if (m < 0) { m = 59; h--; }
        if (h < 0) { h = 23; m = 59; s = 59; }
        return { h, m, s };
      });
    }, 1000);
    return () => clearInterval(t);
  }, []);
  return time;
}

function CountdownBox({ value, label }) {
  return (
    <div className="flex flex-col items-center">
      <div className="bg-slate-900 text-white font-black text-lg w-10 h-10 flex items-center justify-center rounded-lg leading-none">
        {String(value).padStart(2, "0")}
      </div>
      <span className="text-[9px] text-slate-500 mt-0.5">{label}</span>
    </div>
  );
}

export function FlashDealsSection({ products = [], config = {} }) {
  const { h, m, s } = useCountdown(config.countdown_hours || 12);
  if (config.enabled === false) return null;
  const title = config.title || "Today's Flash Deals";
  const maxProducts = config.max_products || 8;
  const catFilter = config.filter_category || "";

  const deals = products
    .filter(p => {
      if (!p.mrp || p.mrp <= (p.sale_price || p.price || 0)) return false;
      if (catFilter) {
        const inCat = (p.category || "").toLowerCase().includes(catFilter.toLowerCase());
        const inCol = (p.collections || []).some(c => c?.toLowerCase().includes(catFilter.toLowerCase()));
        return inCat || inCol;
      }
      return true;
    })
    .sort((a, b) => disc(b.mrp, b.sale_price || b.price) - disc(a.mrp, a.sale_price || a.price))
    .slice(0, maxProducts);

  if (deals.length === 0) return null;

  return (
    <div className="bg-gradient-to-r from-red-50 to-orange-50 py-8 border-t border-b border-red-100">
      <div className="max-w-[1800px] mx-auto px-4">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
          <div className="flex items-center gap-3">
            <Zap className="w-6 h-6 text-red-500 fill-red-500" />
            {config.show_title !== false && (
              <div>
                <h2 className="text-xl font-black text-slate-900">{title}</h2>
              </div>
            )}
            <div className="flex items-center gap-1 ml-2">
              <span className="text-xs text-slate-500 mr-1">Ends In</span>
              <CountdownBox value={h} label="HRS" />
              <span className="text-slate-400 font-bold pb-3">:</span>
              <CountdownBox value={m} label="MIN" />
              <span className="text-slate-400 font-bold pb-3">:</span>
              <CountdownBox value={s} label="SEC" />
            </div>
          </div>
          <Link to="/search?sort=discount" className="text-xs font-semibold text-blue-600 hover:underline flex items-center gap-0.5">
            View All Deals <ChevronRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        {/* Products */}
        <HScrollCarousel>
          {deals.map(p => (
            <MiniProductCard key={p.id} product={p} showAdd={true} />
          ))}
        </HScrollCarousel>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// 4. PROMO BANNERS (2 side by side)
// ──────────────────────────────────────────────────────────────────────────
export function PromoBannersNew({ products = [], config = {} }) {
  if (config.enabled === false) return null;

  // If admin has configured banners, use them dynamically
  if (config.banners && config.banners.length > 0) {
    return (
      <div className="py-8 bg-white">
        <div className="max-w-[1800px] mx-auto px-4 grid grid-cols-1 md:grid-cols-2 gap-5">
          {config.banners.map((banner, i) => {
            const prod = products[i % Math.max(products.length, 1)];
            const img = prod?.images?.[0] || prod?.image || "";
            return (
              <div key={i} className={`relative bg-gradient-to-br ${banner.gradient || "from-orange-400 to-amber-300"} rounded-2xl overflow-hidden p-8 min-h-[180px] flex items-center`}>
                <div className="relative z-10 flex-1">
                  {banner.subtitle && <p className="text-white/80 text-sm font-medium mb-1">{banner.subtitle}</p>}
                  <h3 className="text-2xl font-black text-white leading-tight mb-1">{banner.title}</h3>
                  {banner.discount && (
                    <div className="inline-block bg-white/20 backdrop-blur-sm text-white text-sm font-black px-3 py-1 rounded-lg mb-4">
                      {banner.discount}
                    </div>
                  )}
                  <div className="block">
                    <Link to={banner.link || "/search"} className="inline-flex items-center gap-1 bg-white text-slate-800 font-bold text-sm px-5 py-2.5 rounded-full hover:shadow-lg transition-shadow">
                      {banner.cta_text || "Shop Now"} <ChevronRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
                {banner.image_url && (
                  <div className="absolute right-4 bottom-0 w-36 h-36 opacity-90">
                    <img src={banner.image_url} alt={banner.title} className="w-full h-full object-contain drop-shadow-xl" loading="lazy" decoding="async" />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // Default hardcoded banners when no config.banners
  const kitchenImg = products.find(p => p.name?.toLowerCase().includes("cook") || p.name?.toLowerCase().includes("pan"))?.images?.[0];
  const beautyImg = products.find(p => p.name?.toLowerCase().includes("beauty") || p.name?.toLowerCase().includes("hair"))?.images?.[0];

  return (
    <div className="py-8 bg-white">
      <div className="max-w-[1800px] mx-auto px-4 grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* Banner 1: Monsoon Super Deals */}
        <div className="relative bg-gradient-to-br from-pink-500 via-fuchsia-500 to-rose-400 rounded-2xl overflow-hidden p-8 min-h-[180px] flex items-center">
          <div className="relative z-10 flex-1">
            <p className="text-white/80 text-sm font-medium mb-1">Monsoon</p>
            <h3 className="text-2xl font-black text-white leading-tight mb-1">Super Deals</h3>
            <div className="inline-block bg-white/20 backdrop-blur-sm text-white text-sm font-black px-3 py-1 rounded-lg mb-4">
              UP TO <span className="text-yellow-300">60% OFF</span>
            </div>
            <div className="block">
              <Link to="/search?sort=discount" className="inline-flex items-center gap-1 bg-white text-pink-600 font-bold text-sm px-5 py-2.5 rounded-full hover:shadow-lg transition-shadow">
                Shop Now <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
          {kitchenImg && (
            <div className="absolute right-4 bottom-0 w-36 h-36 opacity-90">
              <img src={kitchenImg} alt="deals" className="w-full h-full object-contain drop-shadow-xl" loading="lazy" decoding="async" />
            </div>
          )}
        </div>

        {/* Banner 2: Beauty & Personal Care */}
        <div className="relative bg-gradient-to-br from-rose-50 to-pink-100 border-2 border-rose-200 rounded-2xl overflow-hidden p-8 min-h-[180px] flex items-center">
          <div className="relative z-10 flex-1">
            <h3 className="text-2xl font-black text-slate-800 leading-tight mb-1">Beauty & Personal Care</h3>
            <p className="text-slate-500 text-sm mb-1">Look Good, Feel Good</p>
            <div className="inline-block bg-rose-500 text-white text-sm font-black px-3 py-1 rounded-lg mb-4">
              UP TO 50% OFF
            </div>
            <div className="block">
              <Link to="/search?q=beauty" className="inline-flex items-center gap-1 bg-rose-500 text-white font-bold text-sm px-5 py-2.5 rounded-full hover:bg-rose-600 transition-colors">
                Shop Now <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
          {beautyImg && (
            <div className="absolute right-4 bottom-0 w-36 h-36 opacity-90">
              <img src={beautyImg} alt="beauty" className="w-full h-full object-contain" loading="lazy" decoding="async" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// 5. NEW ARRIVALS
// ──────────────────────────────────────────────────────────────────────────
export function NewArrivalsSection({ products = [], config = {} }) {
  if (config.enabled === false) return null;
  const title = config.title || "New Arrivals";
  const subtitle = config.subtitle || "Fresh Picks for You";
  const maxProducts = config.max_products || 10;
  const catFilter = config.filter_category || "";

  const newProds = [...products]
    .filter(p => {
      if (!catFilter) return true;
      return (p.category || "").toLowerCase().includes(catFilter.toLowerCase()) ||
        (p.collections || []).some(c => c?.toLowerCase().includes(catFilter.toLowerCase()));
    })
    .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
    .slice(0, maxProducts);

  if (newProds.length === 0) return null;

  return (
    <div className="bg-white py-8 border-t border-slate-100">
      <div className="max-w-[1800px] mx-auto px-4">
        <SectionHeader icon={Zap} title={title} subtitle={subtitle} viewAllTo="/search?sort=newest" flash showTitle={config.show_title !== false} showSubtitle={config.show_subtitle !== false} />
        <HScrollCarousel>
          {newProds.map(p => (
            <div key={p.id} className="relative">
              <span className="absolute top-2 right-2 z-10 bg-green-500 text-white text-[9px] font-black px-1.5 py-0.5 rounded">NEW</span>
              <MiniProductCard product={p} showAdd={true} />
            </div>
          ))}
        </HScrollCarousel>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// 6. FEATURED CATEGORIES
// ──────────────────────────────────────────────────────────────────────────
const CAT_COLORS = [
  "bg-blue-50 border-blue-200",
  "bg-amber-50 border-amber-200",
  "bg-purple-50 border-purple-200",
  "bg-pink-50 border-pink-200",
  "bg-rose-50 border-rose-200",
  "bg-green-50 border-green-200",
];
const CAT_DISCOUNTS = ["Up to 60% Off", "Up to 50% Off", "Up to 60% Off", "Up to 70% Off", "Up to 50% Off", "Up to 60% Off"];

export function FeaturedCategoriesSection({ collections = [], products = [], config = {} }) {
  if (config.enabled === false) return null;
  const title = config.title || "Featured Categories";
  const subtitle = config.subtitle || "Explore our top categories";
  const maxCats = config.max_categories || 6;
  const selectedSlugs = config.selected_slugs || [];

  // If admin has selected specific slugs, use those in order; otherwise show first N
  const cats = selectedSlugs.length > 0
    ? selectedSlugs.map(slug => collections.find(c => (c.slug || c.name?.toLowerCase().replace(/\s+/g, "-")) === slug)).filter(Boolean)
    : collections.slice(0, maxCats);

  if (cats.length === 0) return null;

  const getImage = (col) => {
    const match = products.find(p =>
      (p.collections || []).some(c => c?.toLowerCase() === col.name?.toLowerCase()) ||
      (p.category || "").toLowerCase() === col.name?.toLowerCase()
    );
    return match?.images?.[0] || match?.image || col.image || "";
  };

  return (
    <div className="bg-slate-50 py-8 border-t border-slate-100">
      <div className="max-w-[1800px] mx-auto px-4">
        <SectionHeader title={title} subtitle={subtitle} viewAllTo="/search" showTitle={config.show_title !== false} showSubtitle={config.show_subtitle !== false} />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          {cats.map((col, i) => {
            const img = getImage(col);
            return (
              <Link
                key={col._id || col.id || i}
                to={`/c/${col.slug || col.name?.toLowerCase().replace(/\s+/g, "-")}`}
                className={`${CAT_COLORS[i % CAT_COLORS.length]} border rounded-2xl overflow-hidden group hover:shadow-md transition-shadow`}
              >
                <div className="relative aspect-square overflow-hidden">
                  {img ? (
                    <img src={img} alt={col.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" decoding="async" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-slate-300 text-5xl">🛍</div>
                  )}
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/10">
                    <div className="bg-white/90 rounded-full w-10 h-10 flex items-center justify-center">
                      <Play className="w-4 h-4 text-slate-700 fill-slate-700 ml-0.5" />
                    </div>
                  </div>
                </div>
                <div className="p-3 text-center">
                  <p className="text-sm font-bold text-slate-800">{col.name}</p>
                  <p className="text-xs text-orange-600 font-semibold mt-0.5">{CAT_DISCOUNTS[i % CAT_DISCOUNTS.length]}</p>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// 7. WHY SHOP WITH IMPORTREND
// ──────────────────────────────────────────────────────────────────────────
const WHY_ITEMS_DEFAULT = [
  { icon: "Truck", label: "Free & Fast Delivery", sub: "Across India" },
  { icon: "Package", label: "No MOQ", sub: "Buy from 1 piece" },
  { icon: "BadgePercent", label: "Best Prices", sub: "Direct sourcing" },
  { icon: "ShieldCheck", label: "Secure Payments", sub: "100% safe & secure" },
  { icon: "RotateCcw", label: "Easy Returns", sub: "Hassle free process" },
  { icon: "Headphones", label: "Dedicated Support", sub: "We're here to help" },
];

export function WhyShopSection({ config = {} }) {
  if (config.enabled === false) return null;
  const title = config.title || "Why Shop with IMPORTREND";
  const items = (config.items && config.items.length > 0) ? config.items : WHY_ITEMS_DEFAULT;

  return (
    <div className="bg-white py-10 border-t border-slate-100">
      <div className="max-w-[1800px] mx-auto px-4">
        {config.show_title !== false && (
        <h2 className="text-xl font-black text-slate-900 text-center mb-8">
          {title.includes("IMPORTREND") ? (
            <>
              {title.replace("IMPORTREND", "").trim().replace(/with\s*$/, "with ")}<span className="text-orange-500">IMPORTREND</span>
            </>
          ) : title}
        </h2>
        )}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-6">
          {items.map((item, idx) => {
            const IconComp = ICON_MAP[item.icon] || Truck;
            return (
              <div key={idx} className="flex flex-col items-center text-center gap-2">
                <div className="w-14 h-14 rounded-full bg-orange-50 flex items-center justify-center">
                  <IconComp className="w-6 h-6 text-orange-500" />
                </div>
                <p className="text-sm font-bold text-slate-800">{item.label}</p>
                <p className="text-xs text-slate-400">{item.sub}</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// 8. TOP DEALS THIS WEEK
// ──────────────────────────────────────────────────────────────────────────
const DEAL_TILES_DEFAULT = [
  { label: "Smart Gadgets", price: "Under ₹199", color: "from-slate-800 to-slate-700", q: "gadgets" },
  { label: "Home Essentials", price: "Under ₹299", color: "from-amber-600 to-amber-500", q: "home" },
  { label: "Kitchen Tools", price: "Under ₹149", color: "from-blue-600 to-blue-500", q: "kitchen" },
  { label: "Fashion Accessories", price: "Under ₹99", color: "from-rose-500 to-pink-400", q: "fashion" },
  { label: "Beauty Picks", price: "Under ₹199", color: "from-purple-600 to-violet-500", q: "beauty" },
  { label: "Kids Toys", price: "Under ₹299", color: "from-green-500 to-emerald-400", q: "toys" },
];

export function TopDealsWeekSection({ products = [], config = {} }) {
  if (config.enabled === false) return null;
  const title = config.title || "Top Deals This Week";
  const subtitle = config.subtitle || "Limited time offers with real product videos";
  const tiles = (config.tiles && config.tiles.length > 0) ? config.tiles : DEAL_TILES_DEFAULT;

  const getImg = (q) => {
    const p = products.find(p => p.name?.toLowerCase().includes(q) || (p.category || "").toLowerCase().includes(q));
    return p?.images?.[0] || p?.image || "";
  };

  return (
    <div className="bg-slate-50 py-8 border-t border-slate-100">
      <div className="max-w-[1800px] mx-auto px-4">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-5">
          <div>
            {config.show_title !== false && <h2 className="text-xl font-black text-slate-900">{title}</h2>}
            {config.show_subtitle !== false && <p className="text-xs text-slate-400 mt-0.5">{subtitle}</p>}
          </div>
          <Link to="/search?sort=discount" className="text-xs font-semibold text-blue-600 hover:underline flex items-center gap-0.5">
            View All Deals <ChevronRight className="w-3.5 h-3.5" />
          </Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {tiles.map((tile, i) => {
            const { label, price, color, q } = tile;
            const img = getImg(q || label?.toLowerCase() || "");
            return (
              <Link
                key={i}
                to={`/search?q=${encodeURIComponent(q || label)}`}
                className={`relative bg-gradient-to-br ${color} rounded-xl overflow-hidden aspect-[4/5] flex flex-col justify-end p-4 group hover:shadow-lg transition-shadow`}
              >
                {(tile.image_url || img) && (
                  <div className="absolute inset-0">
                    <img src={tile.image_url || img} alt={label} className="w-full h-full object-cover opacity-40 group-hover:opacity-50 transition-opacity" loading="lazy" decoding="async" />
                  </div>
                )}
                <div className="absolute inset-0 flex items-center justify-center opacity-70 group-hover:opacity-90 transition-opacity">
                  <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center border border-white/30">
                    <Play className="w-4 h-4 text-white fill-white ml-0.5" />
                  </div>
                </div>
                <div className="relative z-10">
                  <p className="text-white font-black text-sm leading-tight">{label}</p>
                  <p className="text-white/80 text-xs font-medium">{price}</p>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// 9. BECOME A RESELLER + DOWNLOAD APP
// ──────────────────────────────────────────────────────────────────────────
const RESELLER_FEATURES_DEFAULT = ["No MOQ", "Best Wholesale Prices", "Faster Delivery", "Full Support"];

export function ResellerAppSection({ config = {} }) {
  if (config.enabled === false) return null;
  const reseller = config.reseller || {};
  const app = config.app || {};

  const resellerTitle = reseller.title || "Become a Reseller";
  const resellerSubtitle = reseller.subtitle || "Start your online business with zero inventory.";
  const resellerFeatures = (reseller.features && reseller.features.length > 0) ? reseller.features : RESELLER_FEATURES_DEFAULT;
  const resellerCtaText = reseller.cta_text || "Register Now";
  const resellerCtaLink = reseller.cta_link || "/pages/become-a-reseller";
  const appTitle = app.title || "Download Our App";
  const appSubtitle = app.subtitle || "Shop anytime, anywhere";

  return (
    <div className="py-8 bg-white border-t border-slate-100">
      <div className="max-w-[1800px] mx-auto px-4 grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* Become a Reseller */}
        <div className="relative bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl overflow-hidden p-8 min-h-[200px]">
          <div className="relative z-10">
            <h3 className="text-2xl font-black text-white mb-1">{resellerTitle}</h3>
            <p className="text-slate-400 text-sm mb-4">{resellerSubtitle}</p>
            <ul className="space-y-1.5 mb-5">
              {resellerFeatures.map((item, i) => (
                <li key={i} className="flex items-center gap-2 text-sm text-white/90">
                  <CheckCircle2 className="w-4 h-4 text-orange-400 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
            <Link to={resellerCtaLink} className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold px-6 py-3 rounded-full transition-colors">
              {resellerCtaText} <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          {/* Decorative laptop icon */}
          <div className="absolute right-6 bottom-6 opacity-20 text-8xl select-none">💻</div>
        </div>

        {/* Download App */}
        <div className="relative bg-gradient-to-br from-blue-50 to-indigo-100 border-2 border-blue-200 rounded-2xl overflow-hidden p-8 min-h-[200px]">
          <div className="relative z-10">
            <h3 className="text-2xl font-black text-slate-800 mb-1">{appTitle}</h3>
            <p className="text-slate-500 text-sm mb-5">{appSubtitle}</p>
            <div className="flex flex-wrap gap-3">
              <a href="#" className="flex items-center gap-2 bg-black text-white px-4 py-2.5 rounded-xl hover:bg-slate-800 transition-colors">
                <Download className="w-4 h-4" />
                <div>
                  <p className="text-[9px] text-white/70 leading-none">GET IT ON</p>
                  <p className="text-sm font-bold leading-tight">Google Play</p>
                </div>
              </a>
              <a href="#" className="flex items-center gap-2 bg-black text-white px-4 py-2.5 rounded-xl hover:bg-slate-800 transition-colors">
                <Apple className="w-4 h-4" />
                <div>
                  <p className="text-[9px] text-white/70 leading-none">DOWNLOAD ON THE</p>
                  <p className="text-sm font-bold leading-tight">App Store</p>
                </div>
              </a>
            </div>
          </div>
          {/* Phone mockup */}
          <div className="absolute right-6 bottom-4 opacity-20 text-8xl select-none">📱</div>
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// SECONDARY BANNER SLIDER (mid-page promotional slideshow)
// ──────────────────────────────────────────────────────────────────────────
export function SecondarySliderSection({ config = {} }) {
  const [cur, setCur] = useState(0);
  const intervalRef = useRef(null);

  const slides = config.slides?.length ? config.slides : [];
  const autoplayMs = config.autoplay_interval || 5000;

  useEffect(() => {
    if (slides.length <= 1) return;
    intervalRef.current = setInterval(() => {
      setCur(c => (c + 1) % slides.length);
    }, autoplayMs);
    return () => clearInterval(intervalRef.current);
  }, [slides.length, autoplayMs]);

  if (config.enabled === false || slides.length === 0) return null;

  const prev = () => {
    clearInterval(intervalRef.current);
    setCur(c => (c - 1 + slides.length) % slides.length);
  };
  const next = () => {
    clearInterval(intervalRef.current);
    setCur(c => (c + 1) % slides.length);
  };

  const slide = slides[cur];

  return (
    <div className="relative overflow-hidden w-full" style={{ aspectRatio: "1600 / 500" }}>
      {/* Slides */}
      {slides.map((sl, i) => (
        <div
          key={i}
          className={`absolute inset-0 transition-opacity duration-700 ${i === cur ? "opacity-100 z-10" : "opacity-0 z-0"}`}
        >
          {sl.image_url ? (
            <img src={sl.image_url} alt={sl.title} className="w-full h-full object-cover" loading="lazy" decoding="async" />
          ) : (
            <div className={`w-full h-full bg-gradient-to-br ${sl.bg_gradient || "from-orange-500 to-amber-400"}`} />
          )}
          {/* Dark overlay for readability */}
          <div className="absolute inset-0 bg-black/30" />
        </div>
      ))}

      {/* Content */}
      <div className="relative z-20 flex items-center justify-center h-full text-center px-8 py-12">
        <div className="max-w-2xl">
          {slide.subtitle && (
            <p className="text-white/80 text-sm font-semibold tracking-widest uppercase mb-2">
              {slide.subtitle}
            </p>
          )}
          <h2 className="text-3xl sm:text-4xl font-black text-white leading-tight mb-3">
            {slide.title}
          </h2>
          {slide.discount_text && (
            <div className="inline-block bg-white/20 backdrop-blur-sm border border-white/30 text-white font-black text-lg px-5 py-1.5 rounded-full mb-5">
              {slide.discount_text}
            </div>
          )}
          {slide.cta_text && (
            <div className="block">
              <Link
                to={slide.cta_link || "/search"}
                className="inline-flex items-center gap-2 bg-white text-slate-900 font-bold px-7 py-3 rounded-full hover:shadow-2xl transition-shadow text-sm"
              >
                {slide.cta_text} <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Navigation Arrows */}
      {slides.length > 1 && (
        <>
          <button
            onClick={prev}
            className="absolute left-4 top-1/2 -translate-y-1/2 z-30 w-10 h-10 bg-white/20 backdrop-blur-sm hover:bg-white/40 text-white rounded-full flex items-center justify-center transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={next}
            className="absolute right-4 top-1/2 -translate-y-1/2 z-30 w-10 h-10 bg-white/20 backdrop-blur-sm hover:bg-white/40 text-white rounded-full flex items-center justify-center transition-colors"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
          {/* Dots */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-30 flex gap-2">
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => setCur(i)}
                className={`w-2.5 h-2.5 rounded-full transition-all ${i === cur ? "bg-white scale-125" : "bg-white/50"}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// ──────────────────────────────────────────────────────────────────────────
const REVIEWS_DEFAULT = [
  { name: "Priya Sharma", location: "Mumbai", text: "Amazing products and fast delivery. Highly recommended!", rating: 5 },
  { name: "Rohit Mehta", location: "Delhi", text: "Best prices and wide variety. Everything I ordered was exactly as shown.", rating: 5 },
  { name: "Sneha Kapoor", location: "Bangalore", text: "Customer support is very helpful. Will definitely shop again.", rating: 4 },
  { name: "Amit Patel", location: "Ahmedabad", text: "Great quality products at wholesale prices. My go-to store for reselling.", rating: 5 },
  { name: "Kavya Nair", location: "Kochi", text: "Smooth shopping experience. Packaging was perfect and delivery was on time.", rating: 5 },
];

export function CustomerReviewsSection({ products = [], config = {} }) {
  const [cur, setCur] = useState(0);
  if (config.enabled === false) return null;
  const sectionTitle = config.title || "What Our Customers Say";
  const sectionSubtitle = config.subtitle || "Real reviews from happy shoppers";
  const reviews = (config.reviews && config.reviews.length > 0) ? config.reviews : REVIEWS_DEFAULT;
  const visible = 3;
  const max = Math.max(0, reviews.length - visible);

  const prev = () => setCur(c => Math.max(0, c - 1));
  const next = () => setCur(c => Math.min(max, c + 1));

  const getProductImg = (i) => products[i % Math.max(products.length, 1)]?.images?.[0] || "";

  return (
    <div className="bg-white py-10 border-t border-slate-100">
      <div className="max-w-[1800px] mx-auto px-4">
        <div className="flex items-end justify-between mb-6">
          <div>
            {config.show_title !== false && <h2 className="text-xl font-black text-slate-900">{sectionTitle}</h2>}
            {config.show_subtitle !== false && <p className="text-xs text-slate-500 mt-0.5">{sectionSubtitle}</p>}
          </div>
          <div className="flex items-center gap-3">
            <Link to="/search" className="text-xs font-semibold text-blue-600 hover:underline flex items-center gap-0.5">
              View All Reviews <ChevronRight className="w-3.5 h-3.5" />
            </Link>
            <div className="flex gap-1">
              <button onClick={prev} disabled={cur === 0} className="w-8 h-8 rounded-full border border-slate-200 flex items-center justify-center hover:border-orange-400 hover:text-orange-500 transition-colors disabled:opacity-30">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button onClick={next} disabled={cur >= max} className="w-8 h-8 rounded-full border border-slate-200 flex items-center justify-center hover:border-orange-400 hover:text-orange-500 transition-colors disabled:opacity-30">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 overflow-hidden">
          {reviews.slice(cur, cur + visible).map((review, i) => {
            const avatar = getAvatar(review.name);
            const avatarColor = review.color || AVATAR_COLORS[(cur + i) % AVATAR_COLORS.length];
            const prodImg = getProductImg(cur + i);
            return (
              <div key={`${review.name}-${i}`} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 hover:shadow-md transition-shadow">
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-10 h-10 rounded-full ${avatarColor} text-white font-black flex items-center justify-center text-sm`}>
                    {avatar}
                  </div>
                  <div>
                    <p className="text-sm font-bold text-slate-800">{review.name}</p>
                    <p className="text-xs text-slate-400">{review.location}</p>
                  </div>
                </div>
                <div className="flex items-center gap-0.5 mb-2">
                  {[...Array(5)].map((_, j) => (
                    <Star key={j} className={`w-3.5 h-3.5 ${j < review.rating ? "text-amber-400 fill-amber-400" : "text-slate-200 fill-slate-200"}`} />
                  ))}
                </div>
                <p className="text-sm text-slate-600 leading-relaxed">{review.text}</p>
                {prodImg && (
                  <div className="mt-3 flex gap-2">
                    <img src={prodImg} alt="product" className="w-12 h-12 rounded-lg object-cover border border-slate-100" loading="lazy" decoding="async" />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
